import baseStyled, { ThemedStyledInterface } from "styled-components";

export const theme = {
  color: {
    blackDark: "#000000",
    black: "#2E3538",
    blue: "#3384CC",
    blueDark: "#48545b",
    blueDarkest: "#245C8F",
    blueLightest: "#ADCEEB",
    blueLink: "#283CBF",
    extremelySubtleTextColor: "#C3CAD5",
    gray100: "#F6F7FB",
    gray200: "#EDEFF1", // gray 500 with 15% opacity
    gray300: "#D4D9DD",
    gray500: "#8897A0",
    gray600: "#6D787D",
    gray800: "#3B4044",
    gray: "#93A1A9",
    grayDark: "#6D787D",
    grayDarkest: "#48545B",
    grayLight: "#BEC6CB",
    grayLightest: "#E8EBED",
    green200: "#DCF2E5",
    green400: "#4CCF81",
    green800: "#094421",
    green: "#3c6",
    greenDark: "#248F47",
    greenDarkest: "#145229",
    greenLight: "#70DA94",
    greenLightest: "#ADEBC2",
    offWhite: "#f6f7f9",
    orange: "#f19f00",
    navColor: "#465156",
    purple: "#0D1B85",
    purpleLight: "#C6CEFF",
    red200: "#FFE6E7",
    red400: "#FC7A80",
    red800: "#642426",
    red: "#CC334B",
    redDark: "#8F2435",
    redDarkest: "#52141E",
    redLight: "#DB7081",
    redLightest: "#EBADB7",
    white: "#ffffff",
    yellow: "#ffd153",
    yellowDark: "#FFBD07",
    yellowDarkest: "#B98800",
    yellowLight: "#FFE5A0",
    yellowLightest: "#FFFAEC",
  },
  fontFamily: '"Open Sans", "Arial", "Helvetica Neue", Helvetica, sans-serif',
  fontSize: {
    xxs: "9px",
    xs: "10px",
    s: "12px",
    m: "14px",
    l: "16px",
    xl: "18px",
    xxl: "20px",
    xxxl: "28px",
    xxxxl: "34px",
    xxxxxl: "36px",
  },
  fontWeight: {
    normal: 400,
    bold: 700,
    superBold: 800,
  },
  gradients: {
    aqua:
      "linear-gradient(65.66deg, rgba(198, 206, 255, 0.85) 0%, rgba(112, 219, 148, 0.85) 100%), #0D1B85;",
    blackEye: "linear-gradient(22.01deg, #3384CC 2.12%, #DB7081 127.83%)",
    blurple: "linear-gradient(198.79deg, #ADCEEB -3%, #8F2435 144.77%)",
    honeydew: "linear-gradient(20.58deg, #70DB94 -6.11%, #FFE5A0 98.05%)",
    mimosa: "linear-gradient(22.01deg, #DB7081 2.12%, #FFE5A0 127.83%)",
    seaFoam: "linear-gradient(22.01deg, #70DB94 2.12%, #ADCEEB 127.83%)",
    spirulina: "linear-gradient(204.25deg, #3384CC -8.98%, #ADEBC2 100.1%)",
    sunset: "linear-gradient(21.44deg, #FFBD07 -19.4%, #EBADB7 81.38%)",
  },
  letterSpacing: {
    xxs: "-0.1px",
    s: "1px",
  },
  nav: {
    width: "200px",
  },
  iconDiameters: {
    xxs: "14px",
    xs: "16px",
    s: "24px",
    m: "26px",
    l: "32px",
    xl: "40px",
    xxl: "48px",
  },
  lineHeight: {
    xxs: "10px",
    xs: "12px",
    s: "14px",
    m: "16px",
    l: "18px",
    xxl: "24px",
    xxxl: "28px",
    xxxxl: "44px",
  },
  radius: {
    s: "4px",
    m: "8px",
    l: "12px",
    xl: "14px",
    xxl: "20px",
    xxxl: "24px",
    superMegaUltra: "32px",
    superMegaUltraHuge: "40px",
  },
  spacing: {
    xxxxs: "2px",
    xxxs: "4px",
    xxs: "8px",
    xs: "12px",
    s: "16px",
    m: "24px",
    xm: "26px",
    l: "32px",
    xl: "40px",
    xxl: "48px",
    xxxl: "56px",
    xxxxl: "60px",
    superMegaUltra: "80px",
    superMegaUltraHuge: "96px",
    superMegaUltraHugelyBig: "110px",
  },
  chartSpacing: {
    xxxs: 4,
    xxs: 8,
    xs: 12,
    s: 16,
    m: 24,
    l: 32,
    xl: 40,
    xxl: 48,
  },
  boxShadow: {
    inset: "inset 0px -5px 19px rgba(46, 53, 56, 0.1)",
    m: "0 2px 4px rgba(46, 53, 56, 0.25)",
    extraMedium: "0 2px 16px rgba(46, 53, 56, 0.15)",
    l: "0 4px 40px rgba(46, 53, 56, 0.15)",
  },
  transitionSpeed: {
    quick: "0.2s cubic-bezier(.22,.68,0,1.71);",
    smooth: "0.35s",
    slow: "0.5s",
    verySlow: "1s",
  },
};

export const redesignTheme = {
  color: {
    blueAlpha100: "#1670df14",
    blueAlpha200: "#1670df29",
    blue500: "#1670df",
    blue600: "#1358b9",
    blue700: "#14458a",
    grayAlpha100: "#6b748514",
    grayAlpha200: "#6b748529",
    grayAlpha300: "#6b74853d",
    gray25: "#f6f6f9",
    gray50: "#edeef3",
    gray300: "#a6acb5",
    gray400: "#898f9a",
    gray500: "#656c7b",
    gray600: "#515661",
    gray700: "#3c3f49",
    gray800: "#2d3039",
    gray900: "#1d1f25",
    greenAlpha100: "#11834214",
    green50: "#d9f2e3",
    green400: "#1e9f54",
    green500: "#118342",
    green600: "#066a35",
    red50: "#fbeaea",
    red400: "#f04747",
    red500: "#d82c2f",
    red600: "#ad2527",
    white: "#ffffff",
    yellow50: "#faf0c7",
    yellow100: "#fbde8e",
    yellow400: "#c28c05",
    yellow500: "#b37e00",
    yellow700: "#5e4003",
  },
  fontFamily: {
    display: "'Plus Jakarta Sans', system-ui, sans-serif",
    interface: "'Inter', system-ui, sans-serif",
  },
  fontSize: {
    1: "12px",
    50: "14px",
    100: "16px",
    150: "18px",
    200: "20px",
    300: "24px",
    400: "28px",
    500: "32px",
    600: "36px",
    700: "40px",
    800: "48px",
    900: "56px",
  },
  fontWeight: {
    normal: 425,
    medium: 500,
    semibold: 600,
    extrabold: 800,
  },
  radius: {
    none: "0px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "24px",
    full: "9999px",
  },
  spacing: {
    1: "2px",
    1.5: "4px",
    2: "6px",
    2.5: "8px",
    3: "10px",
    3.5: "12px",
    4: "14px",
    4.5: "16px",
    5: "20px",
    6: "24px",
    7: "28px",
    8: "32px",
    9: "36px",
    10: "40px",
    12: "48px",
    14: "56px",
    16: "64px",
    18: "72px",
    20: "80px",
    24: "96px",
    28: "112px",
    32: "128px",
    36: "144px",
    40: "160px",
  },
};

export type Theme = typeof theme;
export const styled = baseStyled as ThemedStyledInterface<Theme>;
